import {
  BaseLAM2,
  Configv2_2,
  EVENT_CATEGORY_2,
  EVENT_SUBTYPE_2,
  EVENT_TYPE_2,
  v2Map,
} from "@lululemon/data-collection-types";

// type is very important here!
import type {
  AnaltyicsEventCallback,
  AnalyticsCallbackOptions,
  AnalyticsProperties,
  AnalyticsPropertiesActual,
  JSONPathCallback,
  PayloadType,
} from "@lululemon/data-collection-runtime";
import { pathMutator } from "./transformationEngineV2";
import { deepCopy, MissingFederatedModule } from "../../generalUtils";

import {
  EXPERIMENT_MODULE,
  EXPERIMENT_MODULE_ALT_NAME,
  TRANSFORM_MODULE,
  TRANSFORM_MODULE_ALT_NAME,
  VALIDATION_MODULE,
  VALIDATION_MODULE_ALT_NAME,
} from "../../generalUtils/consts";
import { validateLAMForEvent } from "./validation";
import { logger } from "../../debug";

export function generateCallback<O = Record<string, any>>(
  value: any,
  key?: keyof PayloadType<O>
): JSONPathCallback<PayloadType<O>> {
  return (payload, payloadType, returnObject) => {
    const { parent, parentProperty, hasArrExpr } = returnObject;

    if (hasArrExpr && payload[parentProperty]) {
      payload[parentProperty] = value;
    }
    // If something other than value or object is found otherTypeCallback will be used instead ;)
    if (payloadType === "value") {
      parent[parentProperty] = value;
    }

    if (key) {
      payload[key] = value;
    } else {
      payload = value;
    }
    return payload;
  };
}

type TransformationMap = Map<
  (typeof EVENT_TYPE_2)[keyof typeof EVENT_TYPE_2],
  Map<
    (typeof EVENT_SUBTYPE_2)[keyof typeof EVENT_SUBTYPE_2],
    Map<
      (typeof EVENT_CATEGORY_2)[keyof typeof EVENT_CATEGORY_2],
      ReturnType<typeof pushToLAM>
    >
  >
>;

export default function AutomatedLAMTransformations(options?: {
  initialTransformations?: v2Map<any>;
}) {
  let isLoaded = false;
  let hasErrored: boolean | Error = false;
  const transformationTable: TransformationMap = new Map();

  /**
   * Due to limitations with the Analytics middleware we must manually create all the promises to
   * resolve with Promise all to properly await all of the track events in the lifecycle. Here is
   * a link to the github issue and an example: https://github.com/DavidWells/analytics/issues/357
   *
   * @param eventName Parent event with all handlers inside
   * @param params Event parameters passed in from Analytics library calling function
   * @returns {Array<Promise<void>>}
   */
  async function processEventListenersInTable(
    eventType: (typeof EVENT_TYPE_2)[keyof typeof EVENT_TYPE_2],
    eventSubType: (typeof EVENT_SUBTYPE_2)[keyof typeof EVENT_SUBTYPE_2],
    // This is eventName is our parent structure just don't want to propogate through all code for now
    eventCategory: (typeof EVENT_CATEGORY_2)[keyof typeof EVENT_CATEGORY_2],
    params: Parameters<ReturnType<typeof pushToLAM>>[0]
  ): Promise<undefined | ReturnType<ReturnType<typeof pushToLAM>>> {
    const eventTypeFunction = transformationTable.get(eventType);
    if (eventTypeFunction === undefined) {
      logger.warn(
        `No event type found for ${eventType} in automated transformations`
      );
      return;
    }

    const eventSubTypeFunction = eventTypeFunction.get(eventSubType);
    if (eventSubTypeFunction === undefined) {
      logger.warn(
        `No event sub type found for ${eventSubType} in automated transformations`
      );
      return;
    }

    const eventCategoryFunction = eventSubTypeFunction.get(eventCategory);
    if (eventCategoryFunction === undefined) {
      logger.warn(
        `No event category found for ${eventCategory} in automated transformations`
      );
      return;
    }

    try {
      const validationModule =
        self[VALIDATION_MODULE] || self[VALIDATION_MODULE_ALT_NAME];
      const schemaFn =
        validationModule.automatedValidatorObjects.defaultSchemas?.[
          eventType
        ]?.[eventSubType]?.[eventCategory];

      if (schemaFn === undefined) {
        logger.warn(
          `No schema found for ${eventType} ${eventSubType} ${eventCategory}`
        );
        return;
      }

      const override =
        validationModule.automatedBaseOverrides?.[eventType]?.[eventSubType]?.[
          eventCategory
        ];

      params["payload"].properties.pathMutatorObject = pathMutator(
        deepCopy(schemaFn()),
        (override ?? schemaFn)()
      );

      logger.info(
        `Automated LAM Transformations processing ${eventCategory}`,
        params
      );

      await eventCategoryFunction(params);

      const props = params["payload"];
      const eventObject = props.properties["payload"].event;
      if (
        eventObject.eventType !== undefined &&
        eventObject.eventSubType !== undefined &&
        eventObject.eventName !== undefined
      ) {
        try {
          await (
            self[EXPERIMENT_MODULE] || self[EXPERIMENT_MODULE_ALT_NAME]
          ).prevalidation();
        } catch (e) {
          if (
            e instanceof TypeError &&
            e.message.includes(`reading ${VALIDATION_MODULE}`)
          ) {
            throw new MissingFederatedModule(VALIDATION_MODULE);
          } else {
            logger.error(e);
            return;
          }
        }

        props.properties.validationResult = validateLAMForEvent(
          params["instance"] as unknown as {
            dispatch: (...args) => Promise<void>;
          },
          props.properties.pathMutatorObject.getConfigValues()
            .json as unknown as BaseLAM2,
          {
            eventType,
            eventSubType,
            eventCategory,
          }
        );

        // using to prevent eddlLaunch from firing on automatedData
        if (
          props.options?.writeWithoutValidation === false ||
          props.options?.writeWithoutValidation === undefined
        ) {
          params["payload"].options.writeWithoutValidation = true;
        }

        return params["instance"].track(
          "automatedData",
          params["payload"].properties,
          params["payload"].options
        );
      }
    } catch (e) {
      logger.error(e);
      return;
    }
  }

  return {
    name: "automated-transformations",
    methods: {
      getTransformationTable: () => transformationTable,
    },
    loaded() {
      if (hasErrored || isLoaded) {
        return true;
      }
    },
    initialize() {
      if (
        self[TRANSFORM_MODULE] === undefined &&
        self[TRANSFORM_MODULE_ALT_NAME] === undefined
      ) {
        hasErrored = new MissingFederatedModule(TRANSFORM_MODULE);
      }

      const transformModule =
        self[TRANSFORM_MODULE] || self[TRANSFORM_MODULE_ALT_NAME];
      Object.keys(transformModule.defaultAutomatedEvents).forEach(
        (eventType) => {
          if (!transformationTable.has(eventType)) {
            transformationTable.set(eventType, new Map());
          }
          Object.keys(
            transformModule.defaultAutomatedEvents[eventType]
          ).forEach((eventSubType) => {
            const eventSubTypeMap = transformationTable.get(eventType);
            if (!eventSubTypeMap.has(eventSubType)) {
              eventSubTypeMap.set(eventSubType, new Map());
            }
            Object.keys(
              transformModule.defaultAutomatedEvents[eventType][eventSubType]
            ).forEach((eventName) => {
              if (!eventSubTypeMap.has(eventName)) {
                eventSubTypeMap
                  .get(eventSubType)
                  .set(
                    eventName,
                    pushToLAM(eventType, eventSubType, eventName)
                  );
              }
            });
          });
        }
      );

      isLoaded = true;
    },
    track: async (trackObject) => {
      const {
        payload,
      }: {
        payload: {
          event;
          properties;
        };
      } = trackObject;
      const { event, properties } = payload;
      if (event !== "automated") {
        return;
      }

      const eventTypeToUse = properties.payload?.event?.eventType;
      const eventSubTypeToUse = properties.payload?.event?.eventSubType;
      const eventNameToUse = properties.payload?.event?.eventName;

      if (
        eventTypeToUse === undefined ||
        eventSubTypeToUse === undefined ||
        eventNameToUse === undefined
      ) {
        return;
      }

      return processEventListenersInTable(
        eventTypeToUse,
        eventSubTypeToUse,
        eventNameToUse,
        trackObject
      );
    },
  };
}

type AnalyticsPropertiesActual2 = AnalyticsPropertiesActual & {
  eventType?: string;
  eventSubTyp?: string;
};

export function pushToLAM<
  T extends AnalyticsPropertiesActual2,
  O extends AnalyticsCallbackOptions,
  ETR extends keyof Configv2_2,
  ET extends keyof Configv2_2[ETR],
  EN extends keyof Configv2_2[ETR][ET],
>(
  eventTrigger: ETR,
  eventType: ET,
  eventName: EN
): AnaltyicsEventCallback<T, O> {
  return async (...result) => {
    const {
      instance,
      payload: { event, properties, options, type },
    } = result[0];

    if (type === "track" && event !== "automated") {
      return; // Early return as this will not be run
    }

    if (properties.pathMutatorObject === undefined) {
      throw new Error("Mutator is not in config and was not passed in");
    }

    // // Technically we could json path via $..eventSubType but didn't want to this incase in some object it might get a value not intended to parsed
    // const schemaFromMutator = properties.pathMutatorObject.getConfigValues()
    //   .schema as unknown as BaseLAM2;

    let baseTransform;
    try {
      baseTransform = (
        self[TRANSFORM_MODULE] || self[TRANSFORM_MODULE_ALT_NAME]
      ).defaultAutomatedEvents?.[eventTrigger]?.[eventType]?.[eventName];
    } catch (e) {
      logger.error(e);
      return;
    }

    // Integrates init function from experiments, firing after initial injection and LAM reset, but before adding new data into LAM
    let newProperties;
    try {
      newProperties =
        (await (
          self[EXPERIMENT_MODULE] || self[EXPERIMENT_MODULE_ALT_NAME]
        )?.init(properties.pathMutatorObject)) ?? {};
      if (newProperties && typeof newProperties !== "object") {
        throw new Error(
          "Experiments Module init must return an object, undefined defaults to empty object"
        );
      }
    } catch (e) {
      logger.error(e);
      return;
    }

    type Json = string | number | boolean | { [prop: string]: Json };

    const calculatedProperties: AnalyticsProperties<T> =
      options?.skipExperiments
        ? properties
        : { ...properties, ...newProperties };

    return calculatedProperties.pathMutatorObject.processObject(
      calculatedProperties as unknown as Json,
      baseTransform
    );
  };
}
